import { type ClientContext, ContextClient } from '~/services/contextClient';
import GroupCollectionsClientFactory from './groupCollectionsClient.factory';
import type {
    GroupCollectionsClientInterface,
    GroupCollection,
    GroupCollectionGroupCountsRequest,
} from './groupCollectionsClient.types';
import type { QueryParams } from '~/services/api';
import type { AxiosPromise } from 'axios';

class GroupCollectionsClient extends ContextClient<GroupCollectionsClientInterface> {
    constructor() {
        super(GroupCollectionsClientFactory);
    }

    getGroupCollections(context: ClientContext, params?: QueryParams): AxiosPromise<{ data: Array<GroupCollection> }> {
        const client = this.getClient(context);
        return client.getGroupCollections(params);
    }

    getGroupCollection(context: ClientContext, groupCollectionId: number): AxiosPromise<GroupCollection> {
        const client = this.getClient(context);
        return client.getGroupCollection(groupCollectionId);
    }

    createGroupCollection(context: ClientContext, data: GroupCollection): AxiosPromise<GroupCollection> {
        const client = this.getClient(context);
        return client.createGroupCollection(data);
    }

    updateGroupCollection(
        context: ClientContext,
        groupCollectionId: number,
        data: GroupCollection
    ): AxiosPromise<GroupCollection> {
        const client = this.getClient(context);
        return client.updateGroupCollection(groupCollectionId, data);
    }

    deleteGroupCollection(context: ClientContext, groupCollectionId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.deleteGroupCollection(groupCollectionId);
    }

    getGroupCollectionsGroupCounts(
        context: ClientContext,
        params?: QueryParams
    ): AxiosPromise<{
        counts: Record<number, number>;
    }> {
        const client = this.getClient(context);
        return client.getGroupCollectionsGroupCounts(params);
    }

    getGroupCollectionGroupMemberships(
        context: ClientContext,
        groupCollectionId: number
    ): AxiosPromise<{ memberships: Array<string> }> {
        const client = this.getClient(context);
        if (client.getGroupCollectionGroupMemberships) {
            return client.getGroupCollectionGroupMemberships(groupCollectionId);
        }
        throw new Error(`Get group collection group memberships is not supported by ${client.constructor.name}`);
    }

    getGroupCollectionsGroupMemberships(
        context: ClientContext,
        params?: GroupCollectionGroupCountsRequest
    ): AxiosPromise<{ memberships: Record<number, Array<string>> }> {
        const client = this.getClient(context);
        if (client.getGroupCollectionsGroupMemberships) {
            return client.getGroupCollectionsGroupMemberships(params);
        }
        throw new Error(`Get group collections group memberships is not supported by ${client.constructor.name}`);
    }
}

export default new GroupCollectionsClient();
